import { type yup } from '@framework/validation/initYup.ts';
import { isStringCurrencySchema } from '@framework/validation/methods/stringCurrency.ts';
import { fromPairs } from 'lodash';
import { type ObjectsArrayFieldDatatype } from '../../schema/expressions/datatype/Datatype.ts';
import { isExpressionReferenceOfObjectsArray } from '../../schema/expressions/datatype/isExpressionReferenceOfObjectsArray.ts';
import { evaluateExpression } from '../../schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '../../schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../../schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../../schema/expressions/references/collectExpressionReferences.ts';
import { type IReference } from '../../schema/expressions/references/IReference.ts';
import { type Widget } from '../../schema/widgets/Widget.ts';

export const META_WIDGET_DEFAULT_VALUE = 'widgetDefaultValue@internal';

export function setWidgetDefaultValue<W extends Widget>(
  widget: W,
  schema: yup.AnySchema,
) {
  if (!('defaultValue' in widget)) return schema;
  if (widget.defaultValue == null) return schema;

  const expression = widget.defaultValue;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);
      const result = evaluateExpression(expression, { variables });

      if (result != null && isExpressionReferenceOfObjectsArray(expression)) {
        // Se il valore di default è un riferimento a un campo di tipo `objectsArray`
        // devo trasformarlo in base a quanto configurato nel widget
        // (vedi `defaultValueChildren`)
        if (!Array.isArray(result) || result.length === 0) return schema;

        if (widget.defaultValueChildren == null) {
          // biome-ignore format: <tbd>
          console.warn(`[setWidgetDefaultValue] Widget ${widget.code} has a default value of type objectsArray, but no defaultValueChildren are configured`);
        }

        const resultWithChildren = replaceValueWithChildrenMapping(
          result as any,
          expression.value.field,
          widget.defaultValueChildren!,
        );

        return schema.withMutation((s: any) => {
          s.default(resultWithChildren);
          return s;
        });
      }

      // N.B. Utilizzare != null perché il valore può essere un boolean (`false`)
      if (result != null) {
        return schema.withMutation((s: any) => {
          // I transform di yup non vengono mai applicati al default (vedi
          // `Schema._cast`): per gli schemi valuta-stringa il valore va
          // quindi stringificato alla fonte, così che anche il path
          // "disabilitato", che reinietta `getDefault()`, produca un valore
          // coerente con il tipo.
          s.default(isStringCurrencySchema(s) ? String(result) : result);
          return s;
        });
      }
      return schema;
    },
  );
  // Al momento non viene utilizzato e lo commentiamo per non appesantire il validatore
  // nel caso, può essere utile per identificare la configurazione del valore di default
  // per esempio all'interno di un forEachSchemaNode.
  // .meta({ [META_WIDGET_DEFAULT_VALUE]: widget.defaultValue });
}

function replaceValueWithChildrenMapping(
  items: Array<Record<string, any>>,
  field: ObjectsArrayFieldDatatype,
  childrenMapping: Array<IReference | null>,
) {
  const replaced = items.map(item => {
    return fromPairs(
      childrenMapping
        .map((child, index) => {
          const valueCode = child?.code;
          const sourceCode = field.structure[index]?.code;

          if (!valueCode || !sourceCode) return null;

          return [valueCode, item[sourceCode]];
        })
        .filter((s): s is [string, any] => s != null),
    );
  });

  return replaced;
}
